import { defineStore } from 'pinia'
import { ref, reactive, computed } from 'vue'
import axios from 'axios'
import { fix, sum, divide, multiply, subtract } from 'mathjs'
import { useUserStore } from './UserStore.js'
import { useLetterStore } from './LetterStore.js';
import { useLocalizationStore } from './LocalizationStore.js';
import _ from 'lodash'
import { useRoute, useRouter } from 'vue-router'
import Fuse from 'fuse.js'
import moment from 'moment'
import currency from "currency.js";
import Swal from 'sweetalert2';
import { closeModal } from '../utilities/_helpers'

export const useReportStore = defineStore('ReportStore', {
    state: () => {
        const themeColorCode = {
            poor: ['#ff0000','#28c76f33'],
            fair: ['#fc5005','#28c76f33'],
            good: ['#FFE900','#28c76f33'],
            veryGood: ['#0aba60','#28c76f33'],
            excellent: ['#049c44','#28c76f33']
        };
        const letterUpdated = ref(false);
        return {
            bureauAddressModalId: 'bureau-address-modal',
            letterUpdated,
            statusFailed: 0,
            statusSuccess: 1,
            credentialStatus: [
                'Bad',
                'Good'
            ],
            closeModal: closeModal,
            reportStatus: [
                'Failed',
                'Success'
            ],
            reports: reactive({}),
            creditbureaus: reactive({}),
            userStore: useUserStore(),
            letterStore: useLetterStore(),
            images: reactive({
                transunion: 'https://test-creditfixrr-spaces.nyc3.digitaloceanspaces.com/logos/transunion.png',
                experian: 'https://test-creditfixrr-spaces.nyc3.digitaloceanspaces.com/logos/experian.png',
                equifax: 'https://test-creditfixrr-spaces.nyc3.digitaloceanspaces.com/logos/equifax.png',
                logo: 'https://test-creditfixrr-spaces.nyc3.digitaloceanspaces.com/logos/creditfixrr.jpg',
                banner: 'https://test-creditfixrr-spaces.nyc3.digitaloceanspaces.com/banners/creditfixrr_credit_monitoring_agency.png',
            }),
            thumbnail: {
                transunion: 'https://test-creditfixrr-spaces.nyc3.digitaloceanspaces.com/logos%2Ftu.png',
                experian: 'https://test-creditfixrr-spaces.nyc3.digitaloceanspaces.com/logos%2Fexp.png',
                equifax: 'https://test-creditfixrr-spaces.nyc3.digitaloceanspaces.com/logos%2Feq.png',
            },
            apexchart: {
                themeColorCode: themeColorCode,
                maxSeriesValue: 850,
                defaultBureau: [
                    {
                        credit_score: 0,
                        credit_reporting_agency: {
                            name: 'TransUnion'
                        },
                        lender_rank: 'Not Good',
                    },
                    {
                        credit_score: 0,
                        credit_reporting_agency: {
                            name: 'Experian'
                        },
                        lender_rank: 'Not Good',
                    },
                    {
                        credit_score: 0,
                        credit_reporting_agency: {
                            name: 'Equifax'
                        },
                        lender_rank: 'Not Good',
                    }
                ]
            },
            defaultKeys: [
                'id',
                'credit_bureau_id',
                'furnisher_name',
                'account_number',
                'balance',
                'account_status',
                'payment_status',
                'account_type',
                'account_detail',
                'bureau_code',
                'monthly_payment',
                'past_due',
                'date_opened',
                'date_last_active',
                'date_last_payment',
                'high_credit',
                'credit_limit',
                'last_reported',
                'number_of_months',
                'comments',
                'payment_history',
                'type',
                'late_status',
                'letters',
            ],
            identityiq: 1,
            smartcredit: 2,
            myscoreiq: 3,
            myfreescorenow: 4,
            creditfixrrreports: 5,
            creditMonitoringAgencies: {
                1: {
                    name: 'IdentityIQ',
                    icon: 'https://test-creditfixrr-spaces.nyc3.digitaloceanspaces.com/logos/identityiq.png',
                    description: 'IdentityIQ'
                },
                2: {
                    name: 'SmartCredit',
                    icon: 'https://test-creditfixrr-spaces.nyc3.digitaloceanspaces.com/logos/smartcredit.png',
                    description: 'SmartCredit'
                },
                3: {
                    name: 'MyScoreIQ',
                    icon: 'https://test-creditfixrr-spaces.nyc3.digitaloceanspaces.com/logos/myscoreiq.png',
                    description: 'MyScoreIQ'
                },
                4: {
                    name: 'MyFreeScoreNow',
                    icon: 'https://test-creditfixrr-spaces.nyc3.digitaloceanspaces.com/logos/myfreescorenow.png',
                    description: 'MyFreeScoreNow'
                },
                5: {
                    name: 'Creditfixrrreports',
                    icon: 'https://test-creditfixrr-spaces.nyc3.digitaloceanspaces.com/logos/creditfixrr-reports-logo.png',
                    description: 'Creditfixrrreports'
                }
            },
            months: [
                'Jan',
                'Feb',
                'Mar',
                'Apr',
                'May',
                'Jun',
                'Jul',
                'Aug',
                'Sep',
                'Oct',
                'Nov',
                'Dec'
            ],
            paymentStatus: {
                '1': '30',
                '2': '60',
                '3': '90',
                '4': '120',
                '5': '150',
                '6': '180',
                '9': 'CO',
                'C': 'OK',
                'U': 'U',
                'NR': '--'
            },
            route: useRoute(),
            router: useRouter(),
            localizationStore: useLocalizationStore(),
            disputeReasonTypes: {
                INQUIRY: '1',
                LATE_PAYMENTS: '2',
                CHARGE_OFFS: '3',
                COLLECTIONS: '4',
                DEBT_BUYER_COLLECTIONS: '5',
                STUDENT_LOANS: '6',
                CHILD_SUPPORT: '7',
                REPOSSSESIONS: '8',
                JUDGEMENTS: '9',
                BANKRUPTCY: '10'
            },
            disputeReasonTypeLabel: {
                '1': 'Inquiries',
                '2': 'Late Payments',
                '3': 'Charge Offs',
                '4': 'Collections',
                '5': 'Debt Buyer Collections',
                '6': 'Student Loans',
                '7': 'Child Support',
                '8': 'Reposssesions',
                '9': 'Judgements',
                '10': 'Bankruptcy'
            },
            options: reactive({
                accounts: null,
                publicRecords: null,
                inquiries: null
            }),
            count: reactive({
                deletedAccounts: 0,
                newAccounts: 0,
                newPositiveAccounts: 0,
                newNegativeAccounts: 0,
            }),
            accountComparisonKeys: [
                'credit_bureau_id',
                'furnisher_name',
                'account_number',
                'type'
            ],
            inquiryComparisonKeys: ['credit_bureau', 'creditor_name', 'type'],
            defaultEmptyString: ['-', '--'],
            maxBureauSize: 3, // TransUnion, Experian, Equifax
            defaultColumns: [
                'account_number',
                'account_status',
                'account_type',
                'account_detail',
                'bureau_code',
                'monthly_payment',
                'payment_status',
                'past_due',
                'date_opened',
                'date_last_active',
                'date_last_payment',
                'high_credit',
                'balance',
                'last_reported',
                'number_of_months',
                'comments'
            ],
            dateFormat: 'MM/DD/YYYY',
            selectedDataFurnisherData:reactive({}),
            disputeCenterActiveTab:ref(),
            letterSlug: ref(null),
        }
    },
    persist: {
        enabled: true,
        paths: ['reports', 'options', 'creditbureaus'],
        debug: false
    },
    actions: {
        async getReport() {
            try {
                const email = this.userStore.user.email,
                $this = this;
                this.reports[email] = {
                    haveOldReport: false,
                    haveReport: false,
                    isReportFetch: false,
                    report: {},
                    previousReport: {},
                    filter: {
                        accounts: [],
                        inquiries: []
                    },
                    credit_analysis: {},
                };
                let route;
                if(this.userStore.user.isClient && (!this.route.params.client && !this.route.params.team)){
                    route = this.router.resolve(
                        {
                            name: 'credit-report',
                            params: {
                                client: this.userStore.user.id
                            }
                        }
                    );
                }else{
                    route = this.router.resolve(
                        {
                            name: 'credit-report',
                            params: {
                                client: this.route.params.client,
                                team: this.route.params.team,
                            }
                        }
                    );
                }
                await axios.get(route.href)
                .then(response => {
                    if(response.data.error == true) {
                        toastr.error(response.data.message, 'Error!');
                        return;
                    }
                    this.reports[email].haveOldReport = response.data.data.have_old_report;
                    this.reports[email].haveReport = response.data.data.have_report;
                    this.reports[email].report = response.data.data.report;
                    this.reports[email].credit_analysis = response.data.data.credit_analysis;
                    _.forEach(response.data.data.credit_bureaus, function(creditBureau) {
                        $this.creditbureaus[_.get(creditBureau, 'name')] = creditBureau;
                    });
                    if (!this.reports[email].haveReport) {
                        this.reports[email].report.inquiries = {};
                        this.reports[email].report.accountHistories = {};
                        this.reports[email].report.publicRecords = {};
                        this.reports[email].isReportFetch = true;
                        return;
                    }
                    _.map(this.reports[email].report.inquiries, function (inquiry) {
                        inquiry.checkbox = 0;
                        inquiry.disputeReason = null;
                        inquiry.reason = '';
                        return inquiry;
                    });
                    _.map(this.reports[email].report.accountHistories, function (accountHistory) {
                        accountHistory.checkbox = 0;
                        return accountHistory;
                    });
                    _.map(this.reports[email].report.publicRecords, function (publicRecord) {
                        publicRecord.checkbox = 0;
                        publicRecord.disputeReason = null;
                        publicRecord.reason = '';
                        return publicRecord;
                    });
                    this.reports[email].previousReport = response.data.data.previous_report;
                    this.reports[email].isReportFetch = true;
                })
                .catch(function (error) {
                    toastr.error(error.message, 'Error!');
                });
                let typeOfReports = ['report'];
                if (this.reports[email].haveOldReport) {
                    typeOfReports = ['report', 'previousReport'];
                }
                this.count.newAccounts = 0;
                this.count.deletedAccounts = 0;
                this.count.newNegativeAccounts = 0;
                this.count.newPositiveAccounts = 0;
                this.organizeReport(typeOfReports, this.reports[email]);
                if (this.reports[email].haveOldReport) {
                    const oldAccountHistories = this.reports[email].previousReport.accountHistories;
                    const oldAccountHistoriesFuse = new Fuse(oldAccountHistories, {
                        useExtendedSearch: true,
                        threshold: '0.0',
                        keys: ['account_status', 'payment_status'] // , 'late_status'
                    });
                    let oldPositiveAccounts = oldAccountHistoriesFuse.search("current");
                    oldPositiveAccounts = _.map(oldPositiveAccounts, (account) => { return account.item });
                    let oldNegativeAccounts = oldAccountHistoriesFuse.search("'late|'derogatory|'collection|'charge-off|'unpaid|'repossession|'\"past due\""); // 'unknown|
                    oldNegativeAccounts = _.map(oldNegativeAccounts, (account) => { return account.item });
                    const newAccountHistories = this.reports[email].report.accountHistories;
                    const newAccountHistoriesFuse = new Fuse(newAccountHistories, {
                        useExtendedSearch: true,
                        threshold: '0.0',
                        keys: ['account_status', 'payment_status'] // , 'late_status'
                    });
                    let newPositiveAccounts = newAccountHistoriesFuse.search("current");
                    newPositiveAccounts = _.map(newPositiveAccounts, (account) => { return account.item });
                    let newNegativeAccounts = newAccountHistoriesFuse.search("'late|'derogatory|'collection|'charge-off|'unpaid|'repossession|'\"past due\""); // |'unknown
                    newNegativeAccounts = _.map(newNegativeAccounts, (account) => { return account.item });
                    for (var i = 0; i < newAccountHistories.length; i++) {
                        if (newAccountHistories[i].account_number != '-') {
                            var account = _.find(
                                oldAccountHistories,
                                {
                                    furnisher_name: newAccountHistories[i].furnisher_name,
                                    account_number: newAccountHistories[i].account_number,
                                    type: newAccountHistories[i].type,
                                    credit_bureau_id: newAccountHistories[i].credit_bureau_id
                                }
                            );
                            
                            if (_.isUndefined(account)) {
                                this.count.newAccounts++;
                            }
                        }
                    }
                    for (var i = 0; i < oldAccountHistories.length; i++) {
                        if (oldAccountHistories[i].account_number != '-') {
                            var account = _.find(
                                newAccountHistories,
                                {
                                    furnisher_name: oldAccountHistories[i].furnisher_name,
                                    account_number: oldAccountHistories[i].account_number,
                                    type: oldAccountHistories[i].type,
                                    credit_bureau_id: oldAccountHistories[i].credit_bureau_id
                                }
                            );
                            if (_.isUndefined(account)) {
                                this.count.deletedAccounts++;
                            }
                        }
                    }
                    for (var i = 0; i < oldPositiveAccounts.length; i++) {
                        if (oldPositiveAccounts[i].account_number != '-') {
                            var account = _.find(
                                newNegativeAccounts,
                                {
                                    furnisher_name: oldPositiveAccounts[i].furnisher_name,
                                    account_number: oldPositiveAccounts[i].account_number,
                                    type: oldPositiveAccounts[i].type,
                                    credit_bureau_id: oldPositiveAccounts[i].credit_bureau_id
                                }
                            );
                            if (!_.isUndefined(account)) {
                                this.count.newNegativeAccounts++;
                            }
                        }
                    }
                    for (var i = 0; i < oldNegativeAccounts.length; i++) {
                        if (oldNegativeAccounts[i].account_number != '-') {
                            var account = _.find(
                                newPositiveAccounts,
                                {
                                    furnisher_name: oldNegativeAccounts[i].furnisher_name,
                                    account_number: oldNegativeAccounts[i].account_number,
                                    type: oldNegativeAccounts[i].type,
                                    credit_bureau_id: oldNegativeAccounts[i].credit_bureau_id
                                }
                            );
                            if (!_.isUndefined(account)) {
                                this.count.newPositiveAccounts++;
                            }
                        }
                    }
                }
                this.options.accounts = this.getDisputeReasons([
                    this.disputeReasonTypes.LATE_PAYMENTS,
                    this.disputeReasonTypes.CHARGE_OFFS,
                    this.disputeReasonTypes.COLLECTIONS,
                    this.disputeReasonTypes.DEBT_BUYER_COLLECTIONS,
                    this.disputeReasonTypes.STUDENT_LOANS,
                    this.disputeReasonTypes.REPOSSSESIONS,
                ]);
                this.options.publicRecords = this.getDisputeReasons(
                    [
                        this.disputeReasonTypes.BANKRUPTCY,
                        this.disputeReasonTypes.CHILD_SUPPORT,
                        this.disputeReasonTypes.JUDGEMENTS
                    ]
                );
                this.options.inquiries = this.getDisputeReasons([this.disputeReasonTypes.INQUIRY]);
            } catch (error) {
                console.log(error);
            }
        },
        previousReport(account, column) {
            const email = this.userStore.user.email;
            if (this.reports[email].haveOldReport) {
                var account = _.find(
                    this.reports[email].previousReport.accountHistories,
                    {
                        account_number: account.account_number,
                        type: account.type,
                        credit_bureau_id: account.credit_bureau_id
                    }
                );
                if (!_.isUndefined(account)) {
                    if (column == 'all') {
                        return account
                    }
                    return account[column];
                }
                return "-";
            }
            return "-";
        },
        isNew(accountType, account) {
            if (
                accountType == 'accounts'
                ||
                accountType == 'derogatories'
                ||
                accountType == 'latePayments'
                ||
                accountType == 'listPublicRecords'
            ) {
                let isNew = true, $this = this;
                if (this.reports[this.userStore.user.email].haveOldReport) {
                    _.forEach(this.reports[this.userStore.user.email].previousReport[accountType], function(previousAccount) {
                        if($this.isMatch(previousAccount, account)) {
                            isNew = false;
                        }
                    });
                }
                else {
                    isNew = false;
                }
                return isNew;
            }
            else if (accountType == 'inquiries') {
                if (this.reports[this.userStore.user.email].haveOldReport) {
                    return !_.some(
                        this.reports[this.userStore.user.email].previousReport[accountType],
                        _.pick(account, this.inquiryComparisonKeys)
                    );
                }
                return false;
            }
        },
        isMatch(accountA, accountB) {
            if(
                _.isEqual(
                    _.pick(accountA[0], this.accountComparisonKeys),
                    _.pick(accountB[0], this.accountComparisonKeys)
                )
                &&
                _.isEqual(
                    _.pick(accountA[1], this.accountComparisonKeys),
                    _.pick(accountB[1], this.accountComparisonKeys)
                )
                &&
                _.isEqual(
                    _.pick(accountA[2], this.accountComparisonKeys),
                    _.pick(accountB[2], this.accountComparisonKeys)
                )
            ) {
                return true;
            }
            return false;
        },
        getDeletedRecord(account) {
            let selectedAccount = [];
            _.forEach(this.reports[this.userStore.user.email].previousReport.accounts, function(acc) {
                if (
                    acc[0].furnisher_name == account[0].furnisher_name
                    &&
                    acc[1].furnisher_name == account[1].furnisher_name
                    &&
                    acc[2].furnisher_name == account[2].furnisher_name
                    &&
                    acc[0].account_number == account[0].account_number
                    &&
                    acc[1].account_number == account[1].account_number
                    &&
                    acc[2].account_number == account[2].account_number
                    &&
                    acc[0].balance == account[0].balance
                    &&
                    acc[1].balance == account[1].balance
                    &&
                    acc[2].balance == account[2].balance
                ) {
                    selectedAccount = acc;
                    return;
                }
            });
            return selectedAccount;
        },
        getDeletedPublicRecord(account) {
            let selectedAccount = [];
            _.forEach(this.reports[this.userStore.user.email].previousReport.listPublicRecords, function(acc) {
                if (
                    acc[0].furnisher_name == account[0].furnisher_name
                    &&
                    acc[1].furnisher_name == account[1].furnisher_name
                    &&
                    acc[2].furnisher_name == account[2].furnisher_name
                ) {
                    selectedAccount = acc;
                    return;
                }
            });
            return selectedAccount;
        },
        getAccountDetail(account, type = null) {
            if (_.isNull(type)) {
                if (
                    !account[0].furnisher_name.includes('-')
                    &&
                    !account[0].account_number.includes('-')
                ) {
                    return account[0];
                }
                else if (
                    !account[1].furnisher_name.includes('-')
                    &&
                    !account[1].account_number.includes('-')
                ) {
                    return account[1];
                }
                return account[2];
            }
            if (
                !account[0].furnisher_name.includes('-')
                &&
                !account[0].reference_number.includes('-')
            ) {
                return account[0];
            }
            else if (
                !account[1].furnisher_name.includes('-')
                &&
                !account[1].reference_number.includes('-')
            ) {
                return account[1];
            }
            return account[2];
        },
        getStatus(status, type)
        {
            if (type == this.smartcredit || type == this.creditfixrrreports) {
                if (status == '' || status == ' ') {
                    return this.paymentStatus['NR'];
                }
                return _.has(this.paymentStatus, status) ? this.paymentStatus[status] : status
            }
        },
        formatDate(date)
        {
            if (_.includes(date, '/')) {
                return _.replace(date, /\//g, '-');
            }
            return date;
        },
        borderClass(account) {
            let paymentStatus = this.getAccountDetail(account).payment_status;
            return (
                paymentStatus.includes('Late ') || paymentStatus.includes('Collection') || paymentStatus.includes('Charge') || paymentStatus.includes('Derogatory')
            ) ? 'border-danger' : 'border-success';
        },
        badgeClass(account) {
            let paymentStatus = this.getAccountDetail(account).payment_status;
            return (
                paymentStatus.includes('Late ') || paymentStatus.includes('Collection') || paymentStatus.includes('Charge') || paymentStatus.includes('Derogatory')
            ) ? 'badge-danger' : 'badge-success';
        },
        isAccountSelected(email) {
            let $this = this;
            $this.reports[email].filter.accounts = [];
            $this.reports[email].filter.inquiries = [];
            _.forEach($this.reports[email].report.accounts, function(account) {
                _.forEach([0, 1, 2], function(key) {
                    if (account[key].checkbox == 1) {
                        $this.reports[email].filter.accounts.push(account[key].id);
                    }
                });
            });
            _.forEach($this.reports[email].report.inquiries, function(inquiry) {
                if (inquiry.checkbox == 1) {
                    $this.reports[email].filter.inquiries.push(inquiry.id);
                }
            });
            return sum(this.reports[email].filter.accounts.length, this.reports[email].filter.inquiries.length) > 0;
        },
        isReportedByBureau(account, type = null) {
            if (_.isNull(type)) {
                let isReported = account.account_number == '-' || account.account_number == '--';
                if (isReported) {
                    account.checkbox = 2;
                }
                return isReported;
            }
            let isReported = account.reference_number == '-' || account.reference_number == '--';
            if (isReported) {
                account.checkbox = 2;
            }
            return isReported;
        },
        isViolation(account, index, column, type) {
            if (this.isReported(account[index][column])) {
                let isViolation = null;
                switch (type) {
                    case "date":
                        let matchCount = 1;
                        for (let iteration = 0; iteration < this.maxBureauSize; iteration++) {
                            if(index != iteration && this.isReported(account[iteration][column])) {
                                let defaultDate = moment(account[index][column]);
                                let comparableDate = moment(account[iteration][column]);
                                if (defaultDate.isValid() && comparableDate.isValid() && defaultDate.isSame(comparableDate)) {
                                    matchCount++;
                                }
                            }
                        }
                        isViolation = this.numberOfBureauReported(account, column) != matchCount;
                        return {
                            isViolation: isViolation,
                            message: 'It stipulates when your account is going to fall off. If the CRA\'s are reporting 3 different dates it actually going to fall off on 3 different dates. That is not good.'
                        };
                        break;
                    case "number":
                        return false;
                        break;

                    case "payment_history":
                        isViolation = !_.isEmpty(this.getAccountDetail(account).payment_history)
                            &&
                            (
                                account[0].payment_status.toLowerCase().includes('collection')
                                ||
                                account[1].payment_status.toLowerCase().includes('collection')
                                ||
                                account[2].payment_status.toLowerCase().includes('collection')
                            );
                        return {
                            isViolation: isViolation,
                            message: 'It stipulates when your account is going to fall off. If the CRA\'s are reporting 3 different dates it actually going to fall off on 3 different dates. That is not good.'
                        };

                    case "monthly_payment":
                        isViolation = !_.isEmpty(account[index][column])
                            &&
                            (
                                account[0].payment_status.toLowerCase().includes('charge')
                                ||
                                account[1].payment_status.toLowerCase().includes('charge')
                                ||
                                account[2].payment_status.toLowerCase().includes('charge')
                            );
                            return {
                                isViolation: isViolation,
                                message: "Monthly Payment listed on a Charge-off Account"
                            };

                    case "account_status":
                        isViolation = !_.isEmpty(account[index][column])
                            &&
                            (
                                account[index].payment_status.toLowerCase().includes('collection')
                                ||
                                account[index].account_type.toLowerCase().includes('collection')
                            )
                            &&
                            account[index][column].toLowerCase().includes('open');
                        return {
                            isViolation: isViolation,
                            message: "Open Collection"
                        };

                    case "payment_status":
                        isViolation = !_.isEmpty(account[index][column])
                            &&
                            (
                                account[index].account_status.toLowerCase().includes('collection')
                                ||
                                account[index].account_type.toLowerCase().includes('collection')
                            )
                            &&
                            account[index][column].toLowerCase().includes('late');
                        return {
                            isViolation: isViolation,
                            message: "Late Payments showing on a Collection"
                        };

                    case "past_due":
                        isViolation = !_.isEmpty(account[index][column])
                            &&
                            (
                                account[index].account_status.toLowerCase().includes('collection')
                                ||
                                account[index].account_type.toLowerCase().includes('collection')
                                ||
                                account[index].payment_status.toLowerCase().includes('collection')
                            );
                        return {
                            isViolation: isViolation,
                            message: "Pass Due showing on a Collection"
                        };

                    case "high_credit":
                        isViolation = !_.isEmpty(account[index][column]) && (currency(account[index][column]).value < currency(account[index].balance).value);
                        return {
                            isViolation: isViolation,
                            message: "High Balance is Less Than the Balance"
                        };

                    case "balance":
                        isViolation = !_.isEmpty(account[index][column])
                        &&
                        (currency(account[index][column]).value > 0)
                        &&
                        (
                            account[index].account_status.toLowerCase().includes('collection')
                            ||
                            account[index].account_type.toLowerCase().includes('collection')
                            ||
                            account[index].payment_status.toLowerCase().includes('collection')
                        );
                        return {
                            isViolation: isViolation,
                            message: "Balance showing on a Transfered or Sold Account"
                        };
                }
            }
            return {
                isViolation: false,
                message: ''
            };
        },
        isReported(value) {
            return !_.includes(this.defaultEmptyString, value) && !_.isEmpty(value);
        },
        numberOfBureauReported(account, column) {
            let count = 0;
            if (column == 'all') {
                let $this = this;
                for (let index = 0; index < $this.maxBureauSize; index++) {
                    _.forEach($this.defaultColumns, function(column) {
                        if ($this.isReported(account[index][column])) {
                            count++;
                            return false;
                        }
                    });
                }
                return count;
            }
            for (let index = 0; index < this.maxBureauSize; index++) {
                if (this.isReported(account[index][column])) {
                    count++;
                }
            }
            return count;
        },
        organizeReport(typeOfReports, report) {
            typeOfReports.forEach((reportType) => {
                for (var i = 0; i < report[reportType].accountHistories.length; i+=3) {
                    let creditorId = null;
                    if (report[reportType].creditContacts) {
                        let defaultCreditContact = _.filter(report[reportType].creditContacts, function(creditContact) {
                            if (report[reportType].accountHistories[i].furnisher_name.toLowerCase() == '-') {
                                return false;
                            }
                            if (creditContact.creditor_name.toLowerCase().includes(report[reportType].accountHistories[i].furnisher_name.toLowerCase())) {
                                return true;
                            }
                            else if (creditContact.creditor_name.toLowerCase().includes(report[reportType].accountHistories[i + 1].furnisher_name.toLowerCase())) {
                                return true;
                            }
                            else if (creditContact.creditor_name.toLowerCase().includes(report[reportType].accountHistories[i + 2].furnisher_name.toLowerCase())) {
                                return true;
                            }
                        });
                        if (!_.isUndefined(_.head(defaultCreditContact))) {
                            creditorId = _.head(defaultCreditContact).id;
                        }
                    }
                    report[reportType].accounts.push([
                        _.pick(report[reportType].accountHistories[i], this.defaultKeys),
                        _.pick(report[reportType].accountHistories[i + 1], this.defaultKeys),
                        _.pick(report[reportType].accountHistories[i + 2], this.defaultKeys),
                        0,
                        {
                            isCreditorSelected: 0,
                            creditor: creditorId,
                            disputeReason: null,
                            reason: '',
                        }
                    ]);
                    if (
                        report[reportType].accountHistories[i].account_status.toLowerCase().includes('derogatory')
                        ||
                        report[reportType].accountHistories[i + 1].account_status.toLowerCase().includes('derogatory')
                        ||
                        report[reportType].accountHistories[i + 2].account_status.toLowerCase().includes('derogatory')
                    ) {
                        report[reportType].derogatories.push([
                            _.pick(report[reportType].accountHistories[i], this.defaultKeys),
                            _.pick(report[reportType].accountHistories[i + 1], this.defaultKeys),
                            _.pick(report[reportType].accountHistories[i + 2], this.defaultKeys)
                        ]);
                    }
                    else if (
                        report[reportType].accountHistories[i].account_type.toLowerCase().includes('collection')
                        ||
                        report[reportType].accountHistories[i + 1].account_type.toLowerCase().includes('collection')
                        ||
                        report[reportType].accountHistories[i + 2].account_type.toLowerCase().includes('collection')
                    ) {
                        report[reportType].derogatories.push([
                            _.pick(report[reportType].accountHistories[i], this.defaultKeys),
                            _.pick(report[reportType].accountHistories[i + 1], this.defaultKeys),
                            _.pick(report[reportType].accountHistories[i + 2], this.defaultKeys)
                        ]);
                    }
                    else if (
                        report[reportType].accountHistories[i].account_detail.toLowerCase().includes('collection')
                        ||
                        report[reportType].accountHistories[i + 1].account_detail.toLowerCase().includes('collection')
                        ||
                        report[reportType].accountHistories[i + 2].account_detail.toLowerCase().includes('collection')
                    ) {
                        report[reportType].derogatories.push([
                            _.pick(report[reportType].accountHistories[i], this.defaultKeys),
                            _.pick(report[reportType].accountHistories[i + 1], this.defaultKeys),
                            _.pick(report[reportType].accountHistories[i + 2], this.defaultKeys)
                        ]);
                    }
                    else if (
                        report[reportType].accountHistories[i].late_status.toLowerCase().includes('charge')
                        ||
                        report[reportType].accountHistories[i + 1].late_status.toLowerCase().includes('charge')
                        ||
                        report[reportType].accountHistories[i + 2].late_status.toLowerCase().includes('charge')
                    ) {
                        report[reportType].derogatories.push([
                            _.pick(report[reportType].accountHistories[i], this.defaultKeys),
                            _.pick(report[reportType].accountHistories[i + 1], this.defaultKeys),
                            _.pick(report[reportType].accountHistories[i + 2], this.defaultKeys)
                        ]);
                    }
                    else if (
                        report[reportType].accountHistories[i].payment_status.toLowerCase().includes('charge')
                        ||
                        report[reportType].accountHistories[i + 1].payment_status.toLowerCase().includes('charge')
                        ||
                        report[reportType].accountHistories[i + 2].payment_status.toLowerCase().includes('charge')
                    ) {
                        report[reportType].derogatories.push([
                            _.pick(report[reportType].accountHistories[i], this.defaultKeys),
                            _.pick(report[reportType].accountHistories[i + 1], this.defaultKeys),
                            _.pick(report[reportType].accountHistories[i + 2], this.defaultKeys)
                        ]);
                    }
                    else if (
                        report[reportType].accountHistories[i].payment_status.toLowerCase().includes('collection')
                        ||
                        report[reportType].accountHistories[i + 1].payment_status.toLowerCase().includes('collection')
                        ||
                        report[reportType].accountHistories[i + 2].payment_status.toLowerCase().includes('collection')
                    ) {
                        report[reportType].derogatories.push([
                            _.pick(report[reportType].accountHistories[i], this.defaultKeys),
                            _.pick(report[reportType].accountHistories[i + 1], this.defaultKeys),
                            _.pick(report[reportType].accountHistories[i + 2], this.defaultKeys)
                        ]);
                    }
                    else if (
                        report[reportType].accountHistories[i].payment_status.toLowerCase().includes('foreclosure')
                        ||
                        report[reportType].accountHistories[i + 1].payment_status.toLowerCase().includes('foreclosure')
                        ||
                        report[reportType].accountHistories[i + 2].payment_status.toLowerCase().includes('foreclosure')
                    ) {
                        report[reportType].derogatories.push([
                            _.pick(report[reportType].accountHistories[i], this.defaultKeys),
                            _.pick(report[reportType].accountHistories[i + 1], this.defaultKeys),
                            _.pick(report[reportType].accountHistories[i + 2], this.defaultKeys)
                        ]);
                    }
                    else if (
                        report[reportType].accountHistories[i].payment_status.toLowerCase().includes('repossession')
                        ||
                        report[reportType].accountHistories[i + 1].payment_status.toLowerCase().includes('repossession')
                        ||
                        report[reportType].accountHistories[i + 2].payment_status.toLowerCase().includes('repossession')
                    ) {
                        report[reportType].derogatories.push([
                            _.pick(report[reportType].accountHistories[i], this.defaultKeys),
                            _.pick(report[reportType].accountHistories[i + 1], this.defaultKeys),
                            _.pick(report[reportType].accountHistories[i + 2], this.defaultKeys)
                        ]);
                    }
                    else if (
                        report[reportType].accountHistories[i].payment_status.toLowerCase().includes('unpaid')
                        ||
                        report[reportType].accountHistories[i + 1].payment_status.toLowerCase().includes('unpaid')
                        ||
                        report[reportType].accountHistories[i + 2].payment_status.toLowerCase().includes('unpaid')
                    ) {
                        report[reportType].derogatories.push([
                            _.pick(report[reportType].accountHistories[i], this.defaultKeys),
                            _.pick(report[reportType].accountHistories[i + 1], this.defaultKeys),
                            _.pick(report[reportType].accountHistories[i + 2], this.defaultKeys)
                        ]);
                    }

                    // for Late Payments
                    if (report[reportType].accountHistories[i].type == 2) {
                        if (
                            report[reportType].accountHistories[i].late_status.toLowerCase().includes('late')
                            ||
                            report[reportType].accountHistories[i + 1].late_status.toLowerCase().includes('late')
                            ||
                            report[reportType].accountHistories[i + 2].late_status.toLowerCase().includes('late')
                        ) {
                            report[reportType].latePayments.push([
                                _.pick(report[reportType].accountHistories[i], this.defaultKeys),
                                _.pick(report[reportType].accountHistories[i + 1], this.defaultKeys),
                                _.pick(report[reportType].accountHistories[i + 2], this.defaultKeys)
                            ]);
                        }
                    }
                    else {
                        if (
                            report[reportType].accountHistories[i].payment_status.toLowerCase().includes('late')
                            ||
                            report[reportType].accountHistories[i + 1].payment_status.toLowerCase().includes('late')
                            ||
                            report[reportType].accountHistories[i + 2].payment_status.toLowerCase().includes('late')
                        ) {
                            report[reportType].latePayments.push([
                                _.pick(report[reportType].accountHistories[i], this.defaultKeys),
                                _.pick(report[reportType].accountHistories[i + 1], this.defaultKeys),
                                _.pick(report[reportType].accountHistories[i + 2], this.defaultKeys)
                            ]);
                        }
                        else if (
                            report[reportType].accountHistories[i].payment_status.toLowerCase().includes('past due')
                            ||
                            report[reportType].accountHistories[i + 1].payment_status.toLowerCase().includes('past due')
                            ||
                            report[reportType].accountHistories[i + 2].payment_status.toLowerCase().includes('past due')
                        ) {
                            report[reportType].latePayments.push([
                                _.pick(report[reportType].accountHistories[i], this.defaultKeys),
                                _.pick(report[reportType].accountHistories[i + 1], this.defaultKeys),
                                _.pick(report[reportType].accountHistories[i + 2], this.defaultKeys)
                            ]);
                        }
                    }
                }
                for (var i = 0; i < report[reportType].publicRecords.length; i+=3) {
                    report[reportType].listPublicRecords.push([
                        _.pick(
                            report[reportType].publicRecords[i],
                            ["furnisher_name", "reference_number", "type", "status", "date_filed_reported", "court", "credit_bureau_id", "disputeReason", "reason"]
                        ),
                        _.pick(
                            report[reportType].publicRecords[i + 1],
                            ["furnisher_name", "reference_number", "type", "status", "date_filed_reported", "court", "credit_bureau_id", "disputeReason", "reason"]
                        ),
                        _.pick(
                            report[reportType].publicRecords[i + 2],
                            ["furnisher_name", "reference_number", "type", "status", "date_filed_reported", "court", "credit_bureau_id", "disputeReason", "reason"]
                        ),
                        0
                    ]);
                }
                for (var i = 0; i < report[reportType].inquiries.length; i+=1) {
                    let creditorId = null;
                    if (report[reportType].creditContacts) {
                        let defaultCreditContact = _.filter(report[reportType].creditContacts, function(creditContact) {
                            if (creditContact.creditor_name.toLowerCase().includes(report[reportType].inquiries[i].creditor_name.toLowerCase())) {
                                return true;
                            }
                        });
                        if (!_.isUndefined(_.head(defaultCreditContact))) {
                            creditorId = _.head(defaultCreditContact).id;
                        }
                    }
                    report[reportType].inquiries[i].creditor = creditorId;
                }
            });
        },
        getInquiries(type = null, creditBureau = null) {
            if (_.isNull(type) && _.isNull(creditBureau)) {
                return this.reports[this.userStore.user.email].report.inquiries;
            }
            else if (_.isNull(creditBureau)) {
                return _.filter(this.reports[this.userStore.user.email].report.inquiries, function (inquiry) {
                    if (inquiry.checkbox == type) {
                        return inquiry;
                    }
                });
            }
            else if (_.isNull(type)) {
                return _.filter(this.reports[this.userStore.user.email].report.inquiries, function (inquiry) {
                    if (inquiry.credit_bureau == creditBureau) {
                        return inquiry;
                    }
                });
            }
            return _.filter(this.reports[this.userStore.user.email].report.inquiries, function (inquiry) {
                if (inquiry.checkbox == type && inquiry.credit_bureau == creditBureau) {
                    return inquiry;
                }
            });
        },
        getSelectedAccounts(checkboxType, creditBureauID, type = null, filter = {}) {
            let selectedAccounts = [],
            $this = this;
            if (_.isNull(type)) {
                _.forEach(this.reports[this.userStore.user.email].report.accounts, function(accounts) {
                    _.forEach(accounts, function(account) {
                        if (_.isEmpty(filter)) {
                            if (account.checkbox == checkboxType && creditBureauID == 'all') {
                                selectedAccounts.push(account);
                            }
                            else if (account.checkbox == checkboxType && account.credit_bureau_id == creditBureauID) {
                                account.reason = accounts[4] ? accounts[4].reason : '';
                                selectedAccounts.push(account);
                            }
                        }
                        else {
                            const accountFuse = new Fuse([account], {
                                useExtendedSearch: true,
                                threshold: '0.0',
                                keys: ['account_type', 'account_detail']
                            });
                            let searchedAccount = accountFuse.search(filter);
                            if (
                                account.checkbox == checkboxType
                                &&
                                creditBureauID == 'all'
                                &&
                                searchedAccount.length > 0
                            ) {
                                selectedAccounts.push(account);
                            }
                            else if (
                                account.checkbox == checkboxType
                                &&
                                account.credit_bureau_id == creditBureauID
                                &&
                                searchedAccount.length > 0
                            ) {
                                account.reason = accounts[4] ? accounts[4].reason : '';
                                selectedAccounts.push(account);
                            }
                        }
                    });
                });
            }
            else {
                _.forEach(this.reports[this.userStore.user.email].report.listPublicRecords, function(publicRecords) {
                    _.forEach(publicRecords, function(publicRecord) {
                        if(publicRecord.checkbox == checkboxType && creditBureauID == 'all') {
                            selectedAccounts.push(publicRecord);
                        }
                        else if (publicRecord.checkbox == checkboxType && publicRecord.credit_bureau_id == creditBureauID) {
                            publicRecord.reason = publicRecords[0].reason; 
                            selectedAccounts.push(publicRecord);
                        }
                    });
                });
            }
            return selectedAccounts;
        },
        getCreditorDetail(id) {
            let creditContact = _.filter(this.reports[this.userStore.user.email].report.creditContacts, function(creditContact) {
                return creditContact.id == id;
            });
            if (creditContact.length > 0) {
                return _.head(creditContact);
            }
            return creditContact;
        },
        getcreditContact(searchString) {
            const $this = this;
            const creditContactsFuse = new Fuse(this.reports[this.userStore.user.email].report.creditContacts, {
                useExtendedSearch: true,
                threshold: '0.0',
                keys: ['creditor_name']
            });
            let creditContacts = creditContactsFuse.search(searchString);
            if (creditContacts.length > 0) {
                return _.head(creditContacts).item;
            }
            return null;
        },
        getFilter() {
            if (this.letterStore.isChildSupport()) {
                return `'child|'family`;
            }
            else if (this.letterStore.isStudentLoan()) {
                return `'education`;
            }
            else if (this.letterStore.isRepossession()) {
                return `'"auto loan"`;
            }
            else {
                return '';
            }
        },
        createAttack(type, event) {
            this.closeModal(`#${this.bureauAddressModalId}`);
            Swal.fire({
                title: `${this.localizationStore.translate("attack.modal.title")}`,
                html: `<span class="spinner-border spinner-border-sm  mr-50" role="status" aria-hidden="true"></span> ${this.localizationStore.translate("attack.modal.message")}`,
                showConfirmButton: false,
                allowOutsideClick: false,
                willOpen: function (sweetalert) {
                    sweetalert.querySelector('.swal2-html-container')
                        .classList
                        .add('d-flex', 'align-items-center', 'justify-content-center');
                }
            });
            let $target = event.target;
            if (event.target.tagName != "BUTTON") {
                $target = event.target.closest("button");
            }
            $target.disabled = true;
            let $this = this,
            email = this.userStore.user.email;
            $this.letterUpdated = false
            $this.reports[email].filter.type = type;
            $this.reports[email].filter.selectedInquiries = this.getInquiries(1);
            $this.reports[email].filter.selectedCreditors = $this.getSelectedCreditorAccounts();
            if (
                (type == $this.letterStore.CREDITORS_INQUIRY_METRO_2_LETTER_1)
                &&
                ($this.reports[email].filter.selectedInquiries && ($this.reports[email].filter.selectedInquiries.length == 0))
            ) {
                Swal.close();
                toastr.error("Please Select Inquiries", 'Error!');
                $target.disabled = false;
                return;
            }
            else if (!this.isAccountSelected(email)) {
                Swal.close();
                toastr.error("Please Update Filter", 'Error!');
                $target.disabled = false;
                return;
            }
            let params = {};
            if (this.route.params && this.route.params.client) {
                params.client = this.route.params.client;
            }
            const route = this.router.resolve(
                {
                    name: 'disputes-report',
                    params: params
                }
            );
            this.reports[email].filter.selectedInquiries = _.map(
                this.reports[email].filter.selectedInquiries,
                (inquiry) => {
                    const creditContact = $this.getcreditContact(`'"${inquiry.creditor_name}"`);
                    inquiry.credit_contact = creditContact;
                    return inquiry;
                }
            );
            this.reports[email].filter.selectedAccounts = _.filter(
                _.map(
                    this.reports[email].report.accounts,
                    (accounts) => {
                        let reduceAccountsKey = _.take(accounts, 3);
                        _.forEach(reduceAccountsKey, function(account, key) {
                            if ($this.isReported(account["furnisher_name"])) {
                                let searchString =  `'"${account["furnisher_name"]}"`;
                                reduceAccountsKey[key].credit_contact = $this.getcreditContact(searchString);
                            }
                            else {
                                reduceAccountsKey[key].credit_contact = null;
                            }
                            reduceAccountsKey[key].violation = {
                                'account_status': $this.isViolation(accounts, key, 'account_status', 'account_status').isViolation,
                                'monthly_payment': $this.isViolation(accounts, key, 'monthly_payment', 'monthly_payment').isViolation,
                                'payment_status': $this.isViolation(accounts, key, 'payment_status', 'payment_status').isViolation,
                                'past_due': $this.isViolation(accounts, key, 'past_due', 'past_due').isViolation,
                                'date_last_payment': $this.isViolation(accounts, key, 'date_last_payment', 'date').isViolation,
                                'high_credit': $this.isViolation(accounts, key, 'high_credit', 'high_credit').isViolation,
                                'balance': $this.isViolation(accounts, key, 'balance', 'balance').isViolation,
                                'last_reported': $this.isViolation(accounts, key, 'last_reported', 'date').isViolation,
                            };
                        });
                        return reduceAccountsKey;
                    }
                ),
                (account) => {
                    if (
                        account[0].checkbox == 1
                        ||
                        account[1].checkbox == 1
                        ||
                        account[2].checkbox == 1
                    ) {
                        return account;
                    }
                }
            );
            let address = {};
            _.forEach($this.creditbureaus, function(creditBureau) {
                address[_.get(creditBureau, 'name')] = creditBureau.selected_address;
            });
            this.reports[email].filter.address = address;
            axios.post(
                route.href,
                this.reports[email].filter
            ).then(function(response) {
                Swal.close();
                $target.disabled = false;

                if(response.data.error == true) {
                    $this.letterUpdated = false
                    toastr.error(response.data.message, 'Error!');
                    return;
                }
                $this.letterUpdated = true
                toastr.success(response.data.message, 'Success!');
                return;
            }).catch(function(error) {
                Swal.close();
                $target.disabled = false;
                $this.letterUpdated = false
                toastr.error(error.response.data.response_msg, 'Error!');
            });
        },
        getDataFurnishersCategoryName(id) {
            return this.reports[this.userStore.user.email].report.dataFurnishersCategories[id];
        },
        getChartSeries(creditScore) {
            let min = fix(divide(multiply(
                creditScore,
                100
            ), this.apexchart.maxSeriesValue));
            let max = subtract(100, min);
            return [min, max];
        },
        getCreditScoreDifference(key) {
            if (this.reports[this.userStore.user.email].haveOldReport) {
                return subtract(
                    this.reports[this.userStore.user.email].report.creditScores && this.reports[this.userStore.user.email].report.creditScores[key] ? this.reports[this.userStore.user.email].report.creditScores[key].credit_score : 0,
                    this.reports[this.userStore.user.email].previousReport.creditScores && this.reports[this.userStore.user.email].previousReport.creditScores[key] ? this.reports[this.userStore.user.email].previousReport.creditScores[key].credit_score : 0,
                );
            }
            return 0;
        },
        getChartOptions(creditScore, type = null) {
            let color = this.apexchart.themeColorCode.poor;
            if(creditScore.credit_score  <= 579){
                color = this.apexchart.themeColorCode.poor;
            }else if(creditScore.credit_score  >= 579 && creditScore.credit_score  <= 669){
                color = this.apexchart.themeColorCode.fair;
            }else if(creditScore.credit_score  >= 670 && creditScore.credit_score  <= 739){
                color = this.apexchart.themeColorCode.good;
            }else if(creditScore.credit_score  >= 740 && creditScore.credit_score  <= 799){
                color = this.apexchart.themeColorCode.veryGood;
            }else if(creditScore.credit_score  >= 800 && creditScore.credit_score  <= 850){
                color = this.apexchart.themeColorCode.excellent;
            }
            if (_.isNull(type)) {
                return {
                    chart: {
                        type: 'donut',
                        toolbar: {
                            show: false,
                        },
                    },
                    dataLabels: {
                        enabled: false,
                    },
                    legend: { show: false },
                    comparedResult: [2, -3, 8],
                    labels: ['',''],
                    stroke: { width: 0 },
                    colors: color,
                    grid: {
                        padding: {
                            right: -20,
                            bottom: -8,
                            left: -20,
                        },
                    },
                    plotOptions: {
                        pie: {
                            startAngle: -90,
                            endAngle: 90,
                            offsetY: 10
                        }
                    },
                    responsive: [
                        {
                            breakpoint: 480,
                            options: {
                                chart: {
                                    width: 100
                                },
                                legend: {
                                    position: 'bottom'
                                }
                            }
                        }
                    ],
                    tooltip: {
                        enabled: false,
                    }
                };
            }
            return {
                chart: {
                    height: 350,
                    type: 'radialBar',
                    offsetY: -10
                },
                plotOptions: {
                    radialBar: {
                        size: 150,
                        offsetY: 20,
                        startAngle: -135,
                        endAngle: 135,
                        hollow: {
                            margin: 15,
                            size: '70%',
                            image: this.images[creditScore.credit_reporting_agency.name.toLowerCase()],
                            imageWidth: 120,
                            imageHeight: 120,
                            imageClipped: false,
                            imageOffsetY: -25,
                        },
                        track: {
                            background: '#fff',
                            strokeWidth: '100%'
                        },
                        dataLabels: {
                            name: {
                                show: false,
                                offsetY: -5,
                                color: '#4b9a5e',
                                fontSize: '1rem'
                            },
                            value: {
                                offsetY: 15,
                                color: '#4b9a5e',
                                fontSize: '1.714rem',
                                formatter: function (val) {
                                    return creditScore.credit_score;
                                }
                            },
                        }
                    }
                },
                colors: color,
                fill: {
                    type: 'gradient',
                    gradient: {
                        shade: 'dark',
                        type: 'horizontal',
                        shadeIntensity: 0.5,
                        /* gradientToColors: ['#EA5455'], */
                        inverseColors: true,
                        opacityFrom: 1,
                        opacityTo: 1,
                        stops: [0, 100]
                    }
                },
                stroke: {
                    dashArray: 8
                },
                labels: [creditScore.credit_reporting_agency.name],
            };
        },
        getDisputeReasons(types) {
            let listDisputeReasons = [],
            $this = this;
            _.forEach(types, function(type) {
                let filterReasons = _.filter($this.reports[$this.userStore.user.email].report.disputeReasons, function(disputeReason) {
                    return disputeReason.type == type;
                });
                let mapReasons = _.map(filterReasons, (reason) => { return { id: reason.id, text: reason.description }; });
                listDisputeReasons.push({
                    "text": $this.disputeReasonTypeLabel[type],
                    "children": mapReasons
                });
            });
            return listDisputeReasons;
        },
        getDisputeReasonById(id) {
            return _.find(this.reports[this.userStore.user.email].report.disputeReasons, function(disputeReason) {
                return disputeReason.id == id;
            });
        },
        getBureauImage(bureau) {
            return this.thumbnail[_.toLower(bureau)];
        },
        getCreditScores()
        {
            if (
                this.reports[this.userStore.user.email].report.creditScores
                &&
                this.reports[this.userStore.user.email].report.creditScores.length > 0
            ) {
                return this.reports[this.userStore.user.email].report.creditScores;
            }
            return this.apexchart.defaultBureau;
        }
    },
    getters: {
        countNew: (state) => {
            return (accountType) => {
                if (
                    accountType == 'accounts'
                    ||
                    accountType == 'derogatories'
                    ||
                    accountType == 'latePayments'
                    ||
                    accountType == 'listPublicRecords'
                ) {
                    let total = 0;
                    _.forEach(state.reports[state.userStore.user.email].report[accountType], function(account) {
                        if(state.isNew(accountType, account)) {
                            total++;
                        }
                    });
                    return total;
                }
                else if (accountType == 'inquiries') {
                    let total = 0;
                    _.forEach(state.reports[state.userStore.user.email].report[accountType], function(inquiry) {
                        if (state.isNew(accountType, inquiry)) {
                            total++;
                        }
                    });
                    return total;
                }
            }
        },
        getDeletedAccounts: (state) => {
            return (accountType) => {
                if (
                    accountType == 'accounts'
                    ||
                    accountType == 'derogatories'
                    ||
                    accountType == 'latePayments'
                    ||
                    accountType == 'listPublicRecords'
                ) {
                    let deletedAccounts = [];
                    _.forEach(state.reports[state.userStore.user.email].previousReport[accountType], function(previousLatePayment) {
                        let isMatch = false;
                        _.forEach(state.reports[state.userStore.user.email].report[accountType], function(latePayment) {
                            if(state.isMatch(previousLatePayment, latePayment)) {
                                isMatch = true;
                            }
                        });
                        if (!isMatch) {
                            deletedAccounts.push(previousLatePayment);
                        }
                    });
                    return deletedAccounts;
                }
                if (accountType == 'inquiries') {
                    let deletedAccounts = [], $this = this;
                    _.forEach(state.reports[state.userStore.user.email].previousReport[accountType], function(inquiry) {
                        if(
                            !_.some(
                                state.reports[state.userStore.user.email].report[accountType],
                                _.pick(inquiry, ['credit_bureau', 'creditor_name', 'type'])
                            )
                        ) {
                            deletedAccounts.push(inquiry);
                        }
                    });
                    return deletedAccounts;
                }
            }
        },
        getSelectedCreditorAccounts: (state) => {
            return (filter = {}) => {
                if (!_.isEmpty(filter)) {
                    return _.filter(state.reports[state.userStore.user.email].report.accounts, function (account) {
                        const accountFuse = new Fuse(_.take(account, 3), {
                            useExtendedSearch: true,
                            threshold: '0.0',
                            keys: ['account_type', 'account_detail']
                        });
                        let accounts = accountFuse.search(filter);
                        if (account[4].isCreditorSelected == 1 && accounts.length > 0) {
                            return account;
                        }
                    });
                }
                return _.filter(state.reports[state.userStore.user.email].report.accounts, function (account) {
                    if (account[4].isCreditorSelected == 1) {
                        return account;
                    }
                });
            }
        },
        filterDataFurnishers: (state) => {
            return (selected) => {
                return _.filter(state.reports[state.userStore.user.email].report.dataFurnishers, function (dataFurnisher, index) {
                    if (dataFurnisher.checkbox == selected) {
                        return dataFurnisher;
                    }
                });
            }
        },
        getUniqueSelectedCreditorAccounts: (state) => {
            return () => {
                let selectedCreditorAccounts = state.getSelectedCreditorAccounts(), creditors = [];
                _.map(selectedCreditorAccounts, function (creditorAccounts) {
                    let furnisherName = _.camelCase(state.getCreditorDetail(creditorAccounts[4].creditor).creditor_name);
                    if (!_.isNull(creditorAccounts[4].creditor)) {
                        creditors.push(creditorAccounts[4].creditor);
                    //     creditorIds.push(creditorAccounts[4].creditor);
                    }
                });
                return _.uniq(creditors);
            }
        },

    },
})
